import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faGear } from "@fortawesome/pro-solid-svg-icons";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { AnimatePresence, m } from "motion/react";
import { twJoin } from "tailwind-merge";

import { MotionDropdownPanel } from "src/base-components/MotionDropdownPanel";
import { Icon } from "src/design-system/Icon";

type IconPickerProps = {
  options: Record<string, IconProp>;
  value: string | undefined | null;
  onChange: (value: string | null) => void;
  placeholder?: IconProp;
  disabled?: boolean;
  dataLoc?: string;
  size?: "sm" | "md";
  allowClear?: boolean;
};

const DEFAULT_PLACEHOLDER_ICON = faGear;

export const IconPicker: React.FC<IconPickerProps> = ({
  value,
  options,
  onChange,
  placeholder,
  disabled,
  dataLoc,
  size = "sm",
  allowClear = false,
}) => {
  const hasSelectedIcon = !!value && value in options;
  const selectedIcon = hasSelectedIcon
    ? (options[value] as IconProp)
    : (placeholder ?? DEFAULT_PLACEHOLDER_ICON);

  return (
    <Listbox disabled={disabled} value={value} horizontal onChange={onChange}>
      {({ open }) => (
        <>
          <ListboxButton
            className={twJoin(
              "flex shrink-0 items-center justify-center border border-gray-200 bg-white focus:outline-none data-[hover]:bg-gray-50",
              size === "sm" && "h-8 w-8 rounded-lg",
              size === "md" && "h-15 w-15 rounded-xl",
            )}
            data-loc={dataLoc}
          >
            <m.div
              key={value}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: size === "sm" ? -7 : -14, opacity: 0 }}
              initial={{ y: size === "sm" ? 7 : 14, opacity: 0 }}
              transition={{ duration: 0.2 }}
              layout
            >
              <Icon
                variant={hasSelectedIcon ? "default" : "disabled"}
                icon={selectedIcon}
                size={size}
              />
            </m.div>
          </ListboxButton>

          <AnimatePresence initial={false}>
            {open && (
              <ListboxOptions
                anchor={{
                  to: "bottom",
                  gap: 6,
                  padding: 8,
                }}
                as={MotionDropdownPanel}
                className="focus:outline-none"
              >
                <div className="grid grid-cols-3 gap-2 px-2">
                  {allowClear && (
                    <ListboxOption
                      key="clear"
                      className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg data-[focus]:bg-gray-50 data-[selected]:bg-gray-100"
                      value={null}
                    >
                      <Icon
                        variant="disabled"
                        icon={placeholder ?? DEFAULT_PLACEHOLDER_ICON}
                        size="sm"
                      />
                    </ListboxOption>
                  )}
                  {Object.entries(options).map(([key, icon], index) => (
                    <ListboxOption
                      key={key}
                      className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg data-[focus]:bg-gray-50 data-[selected]:bg-gray-100"
                      data-loc={dataLoc && `${dataLoc}-icon-${index}`}
                      value={key}
                    >
                      <Icon icon={icon} size="sm" />
                    </ListboxOption>
                  ))}
                </div>
              </ListboxOptions>
            )}
          </AnimatePresence>
        </>
      )}
    </Listbox>
  );
};
